// @ts-check
// Capa visual del editor (posición/tamaño), separada de la IR lógica
// (ARC-R1, roadmap §7; DEC-012, docs/generated/decisions.md).
//
// Referencia los nodos del modelo lógico (`editor-model.js`) SOLO por su
// `id` (una cadena opaca): este módulo NO importa `editor-model.js` ni
// `ir-serialization.js`, precisamente para que la dependencia entre la capa
// lógica y la visual sea unidireccional (la visual conoce ids de la lógica;
// la lógica nunca conoce la visual). No incluye DOM ni vistas.
//
// Consumidor real desde TSK-0081 (DEC-028, cierre del Frente B): construido
// en Ola 7 sin ningún llamador de `establecerPosicion`/`obtenerPosicion`
// desde `canvas-view.js` (que dejó de usarlo entre TSK-0060 y TSK-0080,
// mientras el canvas fue una lista vertical secuencial sin coordenadas
// libres). `canvas-view.js` ahora sí llama a las tres funciones de
// CRUD de este módulo para cablear el arrastre libre 2D (ver el comentario
// de cabecera "TSK-0081" en ese archivo); la API de este módulo no cambia
// nada -- ya tenía todo lo necesario.

/**
 * @typedef {{ x: number, y: number, width?: number, height?: number }} PosicionVisual
 */

/**
 * @typedef {{ posiciones: Map<string, PosicionVisual> }} EstadoVisual
 */

/**
 * Crea un estado visual vacío.
 * @returns {EstadoVisual}
 */
export function crearEstadoVisual() {
  return { posiciones: new Map() };
}

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 * @returns {number}
 */
function requerirCoordenadaFinita(valor, nombreCampo) {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) {
    throw new TypeError(`${nombreCampo} debe ser un número finito`);
  }
  return valor;
}

/**
 * Asigna o actualiza la posición/tamaño visual de un nodo identificado por
 * `id`. No valida ni conoce el tipo de nodo lógico: solo guarda datos
 * gráficos indexados por id.
 * @param {EstadoVisual} estado
 * @param {string} id
 * @param {PosicionVisual} posicion
 */
export function establecerPosicion(estado, id, posicion) {
  if (typeof id !== 'string' || id.length === 0) {
    throw new TypeError('id debe ser una cadena no vacía');
  }
  requerirCoordenadaFinita(posicion.x, 'x');
  requerirCoordenadaFinita(posicion.y, 'y');
  if (posicion.width !== undefined) requerirCoordenadaFinita(posicion.width, 'width');
  if (posicion.height !== undefined) requerirCoordenadaFinita(posicion.height, 'height');
  estado.posiciones.set(id, { ...posicion });
}

/**
 * @param {EstadoVisual} estado
 * @param {string} id
 * @returns {PosicionVisual | undefined}
 */
export function obtenerPosicion(estado, id) {
  return estado.posiciones.get(id);
}

/**
 * Elimina la posición asociada a un id (p.ej. al borrar el bloque lógico
 * correspondiente, para no dejar referencias colgantes en la capa visual).
 * @param {EstadoVisual} estado
 * @param {string} id
 * @returns {boolean} `true` si existía una posición y se eliminó.
 */
export function eliminarPosicion(estado, id) {
  return estado.posiciones.delete(id);
}

/**
 * @param {EstadoVisual} estado
 * @returns {string[]}
 */
export function listarIdsConPosicion(estado) {
  return Array.from(estado.posiciones.keys());
}

/**
 * Serializa el estado visual a un objeto plano `{ [id]: PosicionVisual }`,
 * útil para el autoguardado local (TSK-0026). Sigue sin contener ningún
 * dato de la IR lógica: solo posiciones indexadas por id.
 * @param {EstadoVisual} estado
 * @returns {Record<string, PosicionVisual>}
 */
export function estadoVisualAObjeto(estado) {
  return Object.fromEntries(estado.posiciones);
}

/**
 * Reconstruye un `EstadoVisual` desde un objeto plano (contraparte de
 * `estadoVisualAObjeto`), validando cada posición.
 * @param {Record<string, PosicionVisual>} objeto
 * @returns {EstadoVisual}
 */
export function estadoVisualDesdeObjeto(objeto) {
  const estado = crearEstadoVisual();
  for (const [id, posicion] of Object.entries(objeto ?? {})) {
    establecerPosicion(estado, id, posicion);
  }
  return estado;
}
